import styled from 'styled-components';
import { Link } from 'react-router-dom';

export const TaskName = styled(Link)`
  color: ${({ theme }) => theme.palette.primary.main};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

/** The grey card the live prototype renders, one per task and 16px apart; `position: relative` anchors
 *  the whole-card link below. */
export const TaskCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.grey[100]};
`;

export const TaskCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

// The stretched `::after` makes the whole card this link, as the prototype's card click does; the open
// button, positioned and later in the DOM, stays clickable above it. No underline: the prototype has none.
export const TaskCardName = styled(Link)`
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-decoration: none;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
  }
`;

// The assignee column takes the extra width: a name is longer than a date or a status chip.
export const TaskMetaRow = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr;
  gap: 12px;
  align-items: start;
`;

export const MetaField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const MetaLabel = styled.span`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`;

// Fixed height so the avatar, the date text and the status chip share a baseline despite
// their differing intrinsic heights.
export const MetaValue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  min-width: 0;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const MetaValueText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MutedValue = styled.span`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 14px;
  line-height: 20px;
`;

export const DueDateValue = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-size: 14px;
  line-height: 20px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const CreateCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.grey[100]};
`;

/** Roomier than the Tasks card: two fields, so it can afford 16px and 14px labels. */
export const RelateCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.grey[100]};
`;

export const RelateField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const RelateFieldLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const CreateFields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
`;

export const CreateField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const CreateActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export const AddRow = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 4px;
`;

export const HoverActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.12s ease;

  /* Touch has no hover, and opacity still hit-tests: invisible-but-tappable delete. */
  @media (hover: none) {
    opacity: 1;
  }
`;

export const TypeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  & + & {
    margin-top: 24px;
  }
`;

export const SectionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font: inherit;
  text-align: left;
`;

export const SectionHeaderLeft = styled.span`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

export const TypeName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const TypeCount = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const ItemRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 40px;
  padding: 6px 8px;
  border-radius: 8px;
  transition: background 0.12s ease;

  &:hover {
    background: ${({ theme }) => theme.palette.grey[100]};
  }

  &:hover ${HoverActions}, &:focus-within ${HoverActions} {
    opacity: 1;
  }
`;

export const ItemLink = styled(Link)`
  min-width: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.text.primary};
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 8px;
  }

  &:hover {
    text-decoration: underline;
  }
`;

export const ItemName = styled.span`
  min-width: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ItemRight = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const AnswerTag = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: ${({ theme }) => theme.palette.text.secondary};
  background: ${({ theme }) => theme.palette.grey[200]};
  padding: 1px 8px;
  border-radius: 6px;
  white-space: nowrap;
  /* Above the row overlay so the tooltip works, which also means a click here opens nothing. */
  cursor: default;
`;

export const AddArea = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding-top: 16px;
`;
